import React, { useState, useEffect, useCallback } from 'react';
import {
    Search,
    ChevronDown,
    Calendar,
    Download,
    User,
    CreditCard,
    CheckCircle2,
    XCircle,
    Copy,
    ArrowRight,
    Clock,
    CheckCircle,
    X,
    Info,
    Mail,
    Phone,
    MoreVertical,
    Wallet,
    Loader2
} from 'lucide-react';
import { API_BASE_URL } from '../../config';

const WithdrawalRequests = ({ authToken, initialFilter = 'All' }) => {
    const [withdrawalRequests, setWithdrawalRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState(initialFilter);

    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/admin/payouts`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (response.ok) {
                const data = await response.json();
                const mapped = (data.requests || []).map(req => ({
                    id: String(req.request_id),
                    provider: req.provider_info?.name || 'Unknown Provider',
                    role: 'Artist',
                    amount: req.amount || 0,
                    balance: req.provider_info?.available_balance || 0,
                    bank: req.bank_details?.bank_name || 'N/A',
                    accountNo: req.bank_details?.account_number || 'N/A',
                    ifsc: req.bank_details?.ifsc || 'N/A',
                    accountHolder: req.bank_details?.account_holder || 'N/A',
                    date: req.date_requested ? new Date(req.date_requested).toLocaleDateString() : 'N/A',
                    status: (req.status || 'Pending').charAt(0).toUpperCase() + (req.status || 'Pending').slice(1).toLowerCase(),
                    email: req.provider_info?.email,
                    phone: req.provider_info?.contact_number,
                    upi: req.bank_details?.upi_id || 'N/A',
                    wallet: {
                        earned: req.provider_info?.total_earned || 0,
                        pending: req.provider_info?.total_pending_clearance || 0,
                        withdrawn: req.provider_info?.total_paid_out || 0
                    }
                }));
                setWithdrawalRequests(mapped);
            }
        } catch (err) {
            console.error('Failed to fetch withdrawal requests:', err);
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    useEffect(() => {
        setActiveFilter(initialFilter);
    }, [initialFilter]);

    const handleStatusChange = async (requestId, newStatus) => {
        try {
            const apiStatus = newStatus.toLowerCase();
            const response = await fetch(`${API_BASE_URL}/admin/payouts/${requestId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    status: apiStatus,
                    admin_note: `Processed as ${newStatus}`
                })
            });

            if (response.ok) {
                setWithdrawalRequests(prev => prev.map(req =>
                    req.id === requestId ? { ...req, status: newStatus } : req
                ));
                if (selectedRequest && selectedRequest.id === requestId) {
                    setSelectedRequest(prev => ({ ...prev, status: newStatus }));
                }
            }
        } catch (err) {
            console.error('Failed to update status:', err);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getStatusStyle = (status) => {
        const s = status?.toLowerCase();
        switch (s) {
            case 'pending': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'approved': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            case 'paid': return 'bg-green-100 text-green-700 border-green-200';
            case 'processing': return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'rejected': return 'bg-red-50 text-red-600 border-red-100';
            default: return 'bg-gray-50 text-gray-500';
        }
    };

    const filteredRequests = withdrawalRequests.filter(req => {
        const name = req.provider || '';
        const id = req.id || '';
        const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) || id.includes(searchTerm);
        const matchesStatus = activeFilter === 'All' || req.status === activeFilter;
        return matchesSearch && matchesStatus;
    });

    const counts = {
        All: withdrawalRequests.length,
        Pending: withdrawalRequests.filter(r => r.status === 'Pending').length,
        Approved: withdrawalRequests.filter(r => r.status === 'Approved').length,
        Paid: withdrawalRequests.filter(r => r.status === 'Paid').length,
        Rejected: withdrawalRequests.filter(r => r.status === 'Rejected').length,
    };

    return (
        <div className="flex h-full bg-[#F8FAFC]">
            {/* Main Content */}
            <div className={`flex-1 p-6 transition-all duration-300 ${selectedRequest ? 'mr-[400px]' : ''}`}>
                <div className="flex flex-col gap-6">
                    {/* Header */}
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <h1 className="text-2xl font-black text-slate-800 tracking-tight">Withdrawal Requests</h1>
                            <div className="flex gap-2">
                                {['All', 'Pending', 'Approved', 'Paid', 'Rejected'].map(status => (
                                    <button
                                        key={status}
                                        onClick={() => setActiveFilter(status)}
                                        className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${activeFilter === status ? 'bg-amber-100 text-amber-700' : 'bg-white text-slate-500 border border-slate-200'}`}
                                    >
                                        {status} ({counts[status]})
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Filters Bar */}
                    <div className="flex justify-between items-center gap-4">
                        <div className="flex gap-3 flex-1">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search by Provider, ID..."
                                    className="w-full pl-12 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 text-sm font-medium"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-[32px] overflow-hidden shadow-sm border border-slate-100">
                        {loading ? (
                            <div className="p-20 flex flex-col items-center justify-center gap-4 text-slate-400">
                                <Loader2 size={40} className="animate-spin text-blue-500" />
                                <p className="font-bold">Loading withdrawal requests...</p>
                            </div>
                        ) : (
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-100">
                                        <th className="px-6 py-4 text-[11px] font-black text-slate-400 uppercase tracking-widest">Provider</th>
                                        <th className="px-6 py-4 text-[11px] font-black text-slate-400 uppercase tracking-widest text-right">Requested Amount</th>
                                        <th className="px-6 py-4 text-[11px] font-black text-slate-400 uppercase tracking-widest text-right">Available Balance</th>
                                        <th className="px-6 py-4 text-[11px] font-black text-slate-400 uppercase tracking-widest">Bank Details</th>
                                        <th className="px-6 py-4 text-[11px] font-black text-slate-400 uppercase tracking-widest">Request Date</th>
                                        <th className="px-6 py-4 text-[11px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {filteredRequests.length > 0 ? filteredRequests.map((req) => (
                                        <tr
                                            key={req.id}
                                            onClick={() => setSelectedRequest(req)}
                                            className={`group hover:bg-slate-50/80 cursor-pointer transition-colors ${selectedRequest?.id === req.id ? 'bg-amber-50/50' : ''}`}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                                                        <User size={20} className="text-slate-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-black text-slate-800">{req.provider}</p>
                                                        <p className="text-[10px] font-bold text-slate-400">ID: {req.id}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm font-black text-slate-800 text-right">₹{Number(req.amount).toLocaleString()}</td>
                                            <td className="px-6 py-4 text-right">
                                                <span className="text-sm font-black text-emerald-600">₹{Number(req.balance).toLocaleString()}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center">
                                                        <Info size={14} className="text-blue-500" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-700">{req.bank}</p>
                                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">IFSC: {req.ifsc}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-xs font-bold text-slate-500">{req.date}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-between">
                                                    <span className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${getStatusStyle(req.status)}`}>
                                                        • {req.status}
                                                    </span>
                                                    <MoreVertical size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="6" className="p-10 text-center text-slate-400 font-bold">No requests found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* Sidebar Details Drawer */}
            {selectedRequest && (
                <div className="fixed right-0 top-0 h-full w-[400px] bg-white shadow-2xl z-[100] border-l border-slate-100 animate-in slide-in-from-right duration-300">
                    <div className="flex flex-col h-full">
                        {/* Drawer Header */}
                        <div className="p-6 flex justify-between items-center border-b border-slate-50">
                            <h2 className="text-lg font-black text-slate-800 tracking-tight">Withdrawal Request Details</h2>
                            <button onClick={() => setSelectedRequest(null)} className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-50 transition-all"><X size={20} /></button>
                        </div>

                        {/* Drawer Body */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-8">
                            {/* Profile Info */}
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center overflow-hidden">
                                    <User size={28} className="text-slate-400" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-lg font-black text-slate-800">{selectedRequest.provider}</h3>
                                        <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest">{selectedRequest.role}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 mt-1">
                                        <p className="text-xs font-bold text-slate-400 flex items-center gap-1.5"><Mail size={12} /> {selectedRequest.email || 'N/A'}</p>
                                        <p className="text-xs font-bold text-slate-400 flex items-center gap-1.5"><Phone size={12} /> {selectedRequest.phone || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Request Info Card */}
                            <div className="bg-[#FFF9F2] rounded-3xl p-6 border border-amber-100 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-white/40 rounded-full translate-x-8 -translate-y-8" />
                                <div className="relative space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-bold text-amber-900/60 uppercase tracking-widest">Requested Amount</span>
                                        <span className="text-2xl font-black text-slate-900 leading-none">₹{Number(selectedRequest.amount).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm font-bold text-amber-900/60 uppercase tracking-widest">Requested On</span>
                                        <span className="text-sm font-black text-slate-800">{selectedRequest.date}</span>
                                    </div>
                                    <div className="flex gap-2 pt-2">
                                        <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black flex items-center gap-2 ${getStatusStyle(selectedRequest.status)}`}>
                                            <Clock size={12} /> {selectedRequest.status}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Bank Details */}
                            <div className="space-y-4">
                                <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Bank Details</h4>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-slate-50 rounded-xl text-slate-400"><CreditCard size={18} /></div>
                                            <span className="text-sm font-bold text-slate-500">Bank Name</span>
                                        </div>
                                        <span className="text-sm font-black text-slate-800">{selectedRequest.bank}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-slate-50 rounded-xl text-slate-400"><CreditCard size={18} /></div>
                                            <span className="text-sm font-bold text-slate-500">Account No.</span>
                                        </div>
                                        <span className="text-sm font-black text-slate-800">{selectedRequest.accountNo}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-slate-50 rounded-xl text-slate-400"><CreditCard size={18} /></div>
                                            <span className="text-sm font-bold text-slate-500">IFSC Code</span>
                                        </div>
                                        <span className="text-sm font-black text-slate-800 uppercase font-mono tracking-widest">{selectedRequest.ifsc}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-slate-50 rounded-xl text-slate-400"><CreditCard size={18} /></div>
                                            <span className="text-sm font-bold text-slate-500">Account Holder</span>
                                        </div>
                                        <span className="text-sm font-black text-slate-800">{selectedRequest.accountHolder}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-slate-50 rounded-xl text-slate-400"><CreditCard size={18} /></div>
                                            <span className="text-sm font-bold text-slate-500">UPI ID</span>
                                        </div>
                                        <span className="text-sm font-black text-slate-800">{selectedRequest.upi}</span>
                                    </div>
                                    <button
                                        onClick={() => {
                                            const details = `Bank: ${selectedRequest.bank}\nAcc No: ${selectedRequest.accountNo}\nIFSC: ${selectedRequest.ifsc}\nHolder: ${selectedRequest.accountHolder}`;
                                            navigator.clipboard.writeText(details);
                                            alert('Bank details copied to clipboard!');
                                        }}
                                        className="w-full flex items-center justify-center gap-2 py-3 bg-slate-50 border border-dashed border-slate-200 rounded-2xl text-[11px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-100 transition-all font-sans"
                                    >
                                        <Copy size={16} /> Copy Details
                                    </button>
                                </div>
                            </div>

                            {/* Wallet Summary */}
                            <div className="space-y-4 pt-4 border-t border-slate-50">
                                <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Wallet Summary</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-50 p-4 rounded-2xl flex flex-col gap-1">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Wallet size={10} className="text-blue-500" /> Total Earned</p>
                                        <p className="text-md font-black text-slate-800">{formatCurrency(selectedRequest.wallet?.earned)}</p>
                                    </div>
                                    <div className="bg-emerald-50/50 p-4 rounded-2xl flex flex-col gap-1 ring-1 ring-emerald-100">
                                        <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1.5"><CheckCircle2 size={10} /> Available</p>
                                        <p className="text-md font-black text-emerald-600">{formatCurrency(selectedRequest.balance)}</p>
                                    </div>
                                    <div className="bg-slate-50 p-4 rounded-2xl flex flex-col gap-1">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Clock size={10} className="text-amber-500" /> Pending</p>
                                        <p className="text-md font-black text-slate-800">{formatCurrency(selectedRequest.wallet?.pending)}</p>
                                    </div>
                                    <div className="bg-slate-50 p-4 rounded-2xl flex flex-col gap-1">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><CheckCircle size={10} className="text-indigo-500" /> Paid Out</p>
                                        <p className="text-md font-black text-slate-800">{formatCurrency(selectedRequest.wallet?.withdrawn)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-slate-50/80 border-t border-slate-100 grid grid-cols-2 gap-4">
                            <button
                                onClick={() => handleStatusChange(selectedRequest.id, 'Paid')}
                                disabled={selectedRequest.status.toLowerCase() === 'paid'}
                                className={`flex items-center justify-center gap-2 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg transition-all ${selectedRequest.status.toLowerCase() === 'paid'
                                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                                    : 'bg-emerald-600 text-white shadow-emerald-100 hover:bg-emerald-700 active:scale-95'
                                    }`}
                            >
                                <CheckCircle2 size={16} /> Mark as Paid
                            </button>
                            <button
                                onClick={() => handleStatusChange(selectedRequest.id, 'Rejected')}
                                disabled={selectedRequest.status.toLowerCase() === 'rejected' || selectedRequest.status.toLowerCase() === 'paid'}
                                className={`flex items-center justify-center gap-2 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${(selectedRequest.status.toLowerCase() === 'rejected' || selectedRequest.status.toLowerCase() === 'paid')
                                    ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                                    : 'bg-white border border-red-100 text-red-600 hover:bg-red-50 active:scale-95'
                                    }`}
                            >
                                <XCircle size={16} /> Reject Request
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WithdrawalRequests;
